import type { SearchCriteriaInput, VestiaireSearchCriteriaInput } from '@/shared/hooks/queries/generated';
import { itemBadgeList, numberList, sellerBadgeList, stringList } from '@/shared/utils/analysis/vestiaireFilterValues';

import { type AnalysisSegment, isSegmentActive } from './segment';

// ============================================================================
// Relance « zoom » — une recherche lancée depuis une niche / un article
// HÉRITE des critères de l'analyse mère, resserrés par le segment actif.
//
// Depuis le changelog 2026-08-03, les critères de zoom d'une NICHE viennent
// du serveur (`AnalysisNicheStats.relaunchCriteria`, à renvoyer tels quels) —
// la reconstruction manuelle (résolution marque/catégorie par titre) rendait
// des périmètres faux (734 articles annoncés → 3 312 relancés) et a été
// supprimée. La relance depuis un ARTICLE passe par `sourceProductId`
// (recherche fusionnée image + texte, changelog 2026-08-02).
// ============================================================================

const ID_LIST_KEYS = [
  'catalogIds',
  'brandIds',
  'countryIds',
  'excludedBrandIds',
  'conditionIds',
  'colorIds',
  'sizeIds',
  'materialIds',
  'patternIds',
  'brandCollectionIds',
  'videoGamePlatformIds',
  'videoGameRatingIds',
  'internalMemoryCapacityIds',
  'languageBookIds',
  'simLockIds',
] as const;

/** parsedFilter (ids-only, mêmes clés que SearchCriteriaInput) → criteria. */
export const parsedFilterToCriteria = (
  parsedFilter: Record<string, unknown> | null | undefined,
): SearchCriteriaInput => {
  if (!parsedFilter) return {};
  const criteria: Record<string, unknown> = {};

  for (const key of ID_LIST_KEYS) {
    const value = parsedFilter[key];
    if (Array.isArray(value)) {
      const ids = value.filter((v): v is number => typeof v === 'number');
      if (ids.length) criteria[key] = ids;
    }
  }
  if (typeof parsedFilter.priceMin === 'number') criteria.priceMin = parsedFilter.priceMin;
  if (typeof parsedFilter.priceMax === 'number') criteria.priceMax = parsedFilter.priceMax;
  const keywords = parsedFilter.keywords;
  if (Array.isArray(keywords)) {
    const words = keywords.filter((w): w is string => typeof w === 'string');
    if (words.length) criteria.keywords = words;
  }
  const excluded = parsedFilter.excludedKeywords;
  if (Array.isArray(excluded)) {
    const words = excluded.filter((w): w is string => typeof w === 'string');
    if (words.length) criteria.excludedKeywords = words;
  }

  return criteria as SearchCriteriaInput;
};

const VESTIAIRE_ID_LIST_KEYS = [
  'universeIds',
  'catalogRootIds',
  'catalogLevel1Ids',
  'catalogLevel2Ids',
  'brandIds',
  'excludedBrandIds',
  'modelIds',
  'conditionIds',
  'sizeIds',
  'colorIds',
  'materialIds',
  'countryIds',
] as const;

/**
 * vestiaireFilter stocké (ids VC, mêmes clés que VestiaireSearchCriteriaInput) → critères
 * CLASSIQUES : comme `parsedFilterToCriteria`, la source de similarité de l'analyse mère
 * (image, produit source, seuil, description) n'est jamais reportée.
 */
export const vestiaireFilterToCriteria = (
  filter: Record<string, unknown> | null | undefined,
): VestiaireSearchCriteriaInput => {
  if (!filter) return {};
  const criteria: VestiaireSearchCriteriaInput = {};
  for (const key of VESTIAIRE_ID_LIST_KEYS) {
    const ids = numberList(filter[key]);
    if (ids.length) criteria[key] = ids;
  }
  const sellerBadges = sellerBadgeList(filter.sellerBadges);
  if (sellerBadges.length) criteria.sellerBadges = sellerBadges;
  const itemBadges = itemBadgeList(filter.itemBadges);
  if (itemBadges.length) criteria.itemBadges = itemBadges;
  if (typeof filter.priceMin === 'number') criteria.priceMin = filter.priceMin;
  if (typeof filter.priceMax === 'number') criteria.priceMax = filter.priceMax;
  const keywords = stringList(filter.keywords);
  if (keywords.length) criteria.keywords = keywords;
  if (keywords.length && filter.keywordsOperator === 'or') criteria.keywordsOperator = 'or';
  const excluded = stringList(filter.excludedKeywords);
  if (excluded.length) criteria.excludedKeywords = excluded;
  return criteria;
};

/**
 * RelaunchSearchCriteria (objet de query, miroir de SearchCriteriaInput) →
 * input de création. Aucune transformation de valeur : on retire seulement
 * __typename et les champs null (le serveur garantit le reste renvoyable
 * tel quel).
 */
export const relaunchCriteriaToInput = (relaunch: Record<string, unknown>): SearchCriteriaInput => {
  const criteria: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(relaunch)) {
    if (key === '__typename' || value == null) continue;
    criteria[key] = value;
  }
  return criteria as SearchCriteriaInput;
};

/**
 * VestiaireRelaunchCriteria (objet de query) → input de createVestiaireAnalysis, sans
 * transformation. Même contrat que `relaunchCriteriaToInput`.
 */
export const vestiaireRelaunchToInput = (relaunch: Record<string, unknown>): VestiaireSearchCriteriaInput => {
  const criteria: Record<string, unknown> = {};
  for (const [key, value] of Object.entries(relaunch)) {
    if (key === '__typename' || value == null) continue;
    criteria[key] = value;
  }
  return criteria as VestiaireSearchCriteriaInput;
};

interface SegmentMergeable {
  conditionIds?: number[] | null;
  sizeIds?: number[] | null;
  colorIds?: number[] | null;
  priceMin?: number | null;
  priceMax?: number | null;
}

/** Zoom : le segment actif RESSERRE les critères hérités (ids remplacés, prix intersectés) — Vinted ou VC. */
export const mergeSegmentIntoCriteria = <T extends SegmentMergeable>(
  criteria: T,
  segment: AnalysisSegment | undefined,
): T => {
  if (!segment || !isSegmentActive(segment)) return criteria;
  const merged: T = { ...criteria };

  if (segment.conditionIds.length) merged.conditionIds = segment.conditionIds;
  if (segment.sizeIds.length) merged.sizeIds = segment.sizeIds;
  if (segment.colorIds.length) merged.colorIds = segment.colorIds;
  if (segment.priceMin != null) {
    merged.priceMin = Math.max(segment.priceMin, criteria.priceMin ?? Number.NEGATIVE_INFINITY);
  }
  if (segment.priceMax != null) {
    merged.priceMax = Math.min(segment.priceMax, criteria.priceMax ?? Number.POSITIVE_INFINITY);
  }

  return merged;
};

/**
 * Critères « articles similaires » depuis un produit du résultat : critères
 * hérités de l'analyse mère + segment + `sourceProductId`. Le serveur lance
 * une recherche FUSIONNÉE (photo + texte de l'annonce source) — pas besoin de
 * reporter marque ou titre nous-mêmes.
 */
export const buildSimilarItemCriteria = (
  parsedFilter: Record<string, unknown> | null | undefined,
  segment: AnalysisSegment | undefined,
  sourceProductId: string,
): SearchCriteriaInput => ({
  ...mergeSegmentIntoCriteria(parsedFilterToCriteria(parsedFilter), segment),
  sourceProductId,
});

/**
 * Même relance « articles similaires » sur une analyse Vestiaire : critères classiques du
 * `vestiaireFilter` + segment + `sourceProductId` (id d'article VC, jamais un id Vinted).
 */
export const buildVestiaireSimilarItemCriteria = (
  vestiaireFilter: Record<string, unknown> | null | undefined,
  segment: AnalysisSegment | undefined,
  sourceProductId: string,
): VestiaireSearchCriteriaInput => ({
  ...mergeSegmentIntoCriteria(vestiaireFilterToCriteria(vestiaireFilter), segment),
  sourceProductId,
});
